import { mapImageUrl } from './map-image.js';
import type { MapImage, Point } from '../types/app.js';

type Project = (point: Point) => { x: number; y: number };
const sources = new Map<string, HTMLImageElement>();
function imageSource(url: string): HTMLImageElement {
  const existing = sources.get(url);
  if (existing && (!existing.complete || existing.naturalWidth)) return existing;
  const source = new Image();
  source.src = url;
  sources.set(url, source);
  if (sources.size > 64) sources.delete(sources.keys().next().value!);
  return source;
}

export async function prepareMapImages(images: readonly MapImage[], mapId?: string | null): Promise<Map<string, HTMLImageElement>> {
  const prepared = new Map<string, HTMLImageElement>();
  await Promise.all(images.filter(image => !image.hidden).map(async image => {
    const url = mapImageUrl(image, mapId);
    if (!url) throw new Error('The map image is unavailable.');
    const source = imageSource(url);
    await source.decode();
    prepared.set(image.id, source);
  }));
  return prepared;
}

export function drawMapImages(ctx: CanvasRenderingContext2D, images: readonly MapImage[], prepared: ReadonlyMap<string, HTMLImageElement>, project: Project): void {
  for (const image of images) {
    const source = prepared.get(image.id);
    if (image.hidden || !source) continue;
    const [topLeft, topRight, , bottomLeft] = image.points.map(project);
    ctx.save();
    ctx.globalAlpha *= image.opacity;
    ctx.transform((topRight.x - topLeft.x) / source.naturalWidth, (topRight.y - topLeft.y) / source.naturalWidth,
      (bottomLeft.x - topLeft.x) / source.naturalHeight, (bottomLeft.y - topLeft.y) / source.naturalHeight, topLeft.x, topLeft.y);
    ctx.drawImage(source, 0, 0);
    ctx.restore();
  }
}

export function createMapImageRenderer(
  context: () => CanvasRenderingContext2D,
  project: (point: Point) => { x: number; y: number },
  redraw: () => void,
) {
  const mapImageCache = new Map<string, HTMLImageElement | null>();

  function cachedMapImage(image: MapImage, mapId: string | null): HTMLImageElement | null {
    const url = mapImageUrl(image, mapId);
    if (!url) return null;
    const cached = mapImageCache.get(url);
    if (cached !== undefined) return cached;
    const element = imageSource(url);
    mapImageCache.set(url, element);
    element.onload = redraw;
    element.onerror = () => mapImageCache.set(url, null);
    return element;
  }

  function draw(images: MapImage[], mapId: string | null): void {
    const prepared = new Map<string, HTMLImageElement>();
    for (const image of images) {
      if (image.hidden) continue;
      const source = cachedMapImage(image, mapId);
      if (!source?.complete || source.naturalWidth <= 0 || source.naturalHeight <= 0) continue;
      prepared.set(image.id, source);
    }
    drawMapImages(context(), images, prepared, project);
  }

  return { draw };
}
